/**
 * Select role — one <schmancy-filter-chip> per role in the org's catalog:
 * every option stays visible, one click on a chip selects that role — no
 * modal step required just to pick. Each chip's own `.selected` is driven
 * directly off `this.value` (this control never delegates to
 * <schmancy-chips>' internal group state), so nothing here shows "selected"
 * until the caller's OWN `.value` binding genuinely changes — instant for
 * invite-member's ungated pick, only after confirm+Firestore-round-trip for
 * member-detail's gated one.
 *
 * The "view what this role grants" affordance is a SEPARATE <schmancy-button>
 * rendered as a sibling beside each chip — never nested inside the chip's
 * own clickable button — so the select target and the preview target are
 * two structurally distinct elements with no shared interactive ancestor: a
 * click on one can never be misrouted to the other, and no defensive
 * `stopPropagation()` is needed to keep them apart. Clicking it opens the
 * role's full grant tree in <role-permissions>; clicking the chip itself
 * still just selects it. EVERY chip gets the preview affordance, not only
 * the selected one — previewing is how you decide which chip to click, and
 * in member-detail a click fires an immediate grant change, so looking
 * before committing matters even more. Viewing stays reachable even when
 * `disabled` — read access is independent of write access, and the preview
 * button is never itself disabled.
 *
 * Shared by invite-member and member-detail — one `.value` in / `change`
 * out contract, so callers no longer render their own role picker. Folds in
 * "+ Create new role" — a distinct create-action, not a pick-among-existing
 * affordance.
 *
 * With 0/1 roles in the catalog there is no real choice — the chip row is
 * replaced by an auto-select side effect (1 role) or nothing beyond
 * "+ Create new role" (0 roles). That link stays reachable unless the
 * caller marks the whole control `disabled`.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { Subject, switchMap, takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { roleDraft, rolesState } from "../members.state.ts";
import "./role-form.view.ts";
import "./role-permissions.view.ts";

@schmancyElement("select-role")
export class SelectRole extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) value = "";
	@property({ type: Boolean }) disabled = false;
	@property({ attribute: false }) pendingRoleId = "";
	@property({ attribute: false }) failedRoleId = "";

	/**
	 * View-permissions is a single exclusive preview session per instance — at
	 * most one <role-permissions> overlay is ever open. Each chip's adjacent
	 * preview button only dispatches an intent; switchMap cancels (closes)
	 * whatever overlay is currently open before opening the newly-requested one.
	 */
	private readonly viewPermissions$ = new Subject<string>();

	override connectedCallback(): void {
		super.connectedCallback();
		const roles = rolesState.value;
		const [onlyRole] = roles;
		if (roles.length === 1 && onlyRole && onlyRole.id !== this.value)
			this.emitChange(onlyRole.id);

		this.viewPermissions$
			.pipe(
				switchMap((roleId) =>
					show(
						() => html`<role-permissions .roleId=${roleId}></role-permissions>`,
					),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe();
	}

	private emitChange(roleId: string): void {
		if (this.disabled || this.pendingRoleId) return;
		this.dispatchEvent(
			new CustomEvent("change", {
				detail: { value: roleId },
				bubbles: true,
				composed: true,
			}),
		);
	}

	private createNewRole(): void {
		roleDraft.replace({
			roleId: undefined,
			name: "",
			perms: new Set(),
			editing: true,
		});
		// Nested on top of whatever's already open (schmancy overlays stack) —
		// the caller's own overlay (e.g. invite-member) stays open underneath.
		show(() => html`<role-form standalone></role-form>`)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	private renderCreateRoleLink(): TemplateResult {
		return html`
			<schmancy-button variant="text" size="sm" ?disabled=${!!this.pendingRoleId} class="self-start" @click=${() => this.createNewRole()}>
				<schmancy-icon slot="prefix" aria-hidden="true">add</schmancy-icon>
				${this.t("admin.members.selectRole.createNewRole")}
			</schmancy-button>
		`;
	}

	override render(): TemplateResult {
		const roles = rolesState.value;

		if (roles.length <= 1) {
			return when(
				!this.disabled,
				() => this.renderCreateRoleLink(),
				() => html``,
			);
		}

		return html`
			<div class="flex flex-col gap-2">
				<div class="flex flex-wrap gap-2">
					${repeat(
						roles,
						(r) => r.id,
						(role) => {
							const permissionsLabel =
								role.permissions.length === 1
									? this.t("admin.members.common.permissionsCountOne", {
											count: role.permissions.length,
										})
									: this.t("admin.members.common.permissionsCountOther", {
											count: role.permissions.length,
										});
							return html`
								<div class="inline-flex items-center gap-1">
									<schmancy-filter-chip
										.value=${role.id}
										.selected=${role.id === this.value}
										?disabled=${this.disabled}
										.busy=${this.pendingRoleId === role.id}
										?readonly=${!!this.pendingRoleId && this.pendingRoleId !== role.id}
										title=${this.failedRoleId === role.id ? this.t("admin.members.detail.roleUpdateFailed") : ""}
										@change=${(
											e: CustomEvent<{ value: string; selected: boolean }>,
										) => {
											// The chip's own change is composed, so it escapes this
											// shadow root and reaches the consumer a second time,
											// past every guard below. Stopped here, so the only
											// `change` a consumer ever sees is the one this handler
											// decided to send.
											e.stopPropagation();
											if (this.disabled || this.pendingRoleId) return;
											// A re-click on the already-selected chip fires selected:false
											// — a role, once picked, only changes by picking a different
											// one, never by deselecting to nothing.
											if (!e.detail.selected) return;
											if (e.detail.value === this.value) return;
											this.emitChange(e.detail.value);
										}}
									>
										<span style="display:grid"><span style=${this.failedRoleId === role.id ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failedRoleId === role.id ? "true" : "false"}>${role.name}</span><span style=${this.failedRoleId === role.id ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failedRoleId === role.id ? "false" : "true"}>${role.name} · ${this.t("admin.common.retry")}</span></span>
									</schmancy-filter-chip>
									<schmancy-button
										variant="text"
										size="xxs"
										aria-label=${this.t(
											"admin.members.selectRole.viewGrantsAriaLabel",
											{
												role: role.name,
												permissions: permissionsLabel,
											},
										)}
										@click=${() => this.viewPermissions$.next(role.id)}
									>
										(${permissionsLabel})
										<schmancy-icon slot="suffix" class="text-xs">chevron_right</schmancy-icon>
									</schmancy-button>
								</div>
							`;
						},
					)}
				</div>

				${when(!this.disabled, () => this.renderCreateRoleLink())}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"select-role": SelectRole;
	}
}
